/**
 * Verdi Chart Spec — v1
 *
 * A DashboardSpec is a complete render description for the data playground.
 * It owns everything the graph needs to render: which streams, how they look,
 * how they're grouped, what time window, and what thresholds to draw.
 *
 * What lives here:
 *   - Explicit stream IDs (always resolved before the spec is created/saved)
 *   - Display config per stream (color, label, axis, interpolation, group)
 *   - Panel order (the vertical stack) and panel-level config
 *   - Shared time range
 *
 * What does NOT live here:
 *   - Query / discovery logic — that's getZoneSpec() / getDeviceSpec(), templates, the builder UI.
 *     Those are functions that *produce* a DashboardSpec; they're not part of the schema.
 *   - Fetch resolution (raw / 1m / 5m / 1h) — determined by the data layer based on
 *     the time range and viewport width. Not a render concern.
 *   - Builder UI state (expanded tree nodes, scroll position, modal open state) — ephemeral,
 *     lives in component state only.
 */

// ---------------------------------------------------------------------------
// Version
// ---------------------------------------------------------------------------

import type { BucketAggregate } from "verdi-common/schemas/time-series/data-stream-query";

export const SPEC_VERSION = 1 as const;

// ---------------------------------------------------------------------------
// Time range
// ---------------------------------------------------------------------------

/**
 * Relative durations anchor the end to "now" and slide forward automatically.
 * This is the natural mode for live monitoring.
 * Absolute mode is for fixed historical windows (e.g. a saved investigation).
 */
export type RelativeDuration = "5m" | "1h" | "6h" | "1d" | "7d" | "4d" | "30d" | "90d" | "1y";

export type TimeRange =
    | { mode: "relative"; duration: RelativeDuration }
    | { mode: "absolute"; start: string; end: string }; // ISO 8601

// ---------------------------------------------------------------------------
// Axes
// ---------------------------------------------------------------------------

export enum AxisSide {
    Left = "left",
    Right = "right",
}

/**
 * Each panel declares its own y-axes. Any number of left axes are allowed (one visible at a time);
 * at most one optional right axis. Stream entries reference an axis by id.
 *
 * unitCategory maps to the unit system (e.g. "volumetricSoilMoisture", "liquidFlow",
 * "liquidPressure") and determines which unit preference is applied at render time.
 * It's a plain string, not the UnitCategory union: the value originates from the wire
 * and may be a category verdi-common doesn't yet enumerate. Render-time consumers narrow
 * it via isKnownUnitCategory and degrade gracefully (raw values) when it's unknown.
 */
export interface AxisSpec {
    id: string;
    unitCategory: string;
    side: AxisSide;
    label?: string;
}

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

/**
 * How the group's members are combined and rendered.
 *
 * "basic"              — visual grouping only; members render individually (no computation)
 *
 * Aggregates ("result only" — member series hidden, derived series uses group color/axisId):
 * "sum"                — single derived line: sum of all member values at each timestamp
 * "average"            — single derived line: mean of all member values at each timestamp
 *
 * Aggregates ("members visible" — members still drawn alongside the derived series):
 * "stack"              — cumulative stacked area; top edge = sum. Only meaningful when
 *                        summing is physically valid (e.g. flow contributions to a zone total).
 *                        Member order follows streams[]. NOT for depth fans — use DepthOffsetPanelSpec.
 * "mean-with-shadows"  — mean line prominent; member lines rendered as grey shadows behind it.
 *
 * Symmetry: stack : sum  ::  mean-with-shadows : average
 *   (each aggregate has a "result only" and a "members visible" variant)
 */
export type GroupType = "basic" | "sum" | "average" | "stack" | "mean-with-shadows";

export interface GroupSpec {
    id: string;
    label?: string;
    type: GroupType;
    /** Color of the derived series ("sum" and "average" only). */
    color?: string;
    /** Axis the derived series is plotted on ("sum" and "average" only). References AxisSpec.id. */
    axisId?: string;
}

// ---------------------------------------------------------------------------
// Streams
// ---------------------------------------------------------------------------

/**
 * DATA fields (affect what is fetched — include in cache key):
 *   streamId
 *
 * VIEW fields (affect only rendering — changing these must NOT trigger a refetch):
 *   color, name, sensorClusterName, sensorElementName, axisId, groupId
 *
 * The cache key is streamId + resolution (getStreamCacheKey in stream-cache). Time range is
 * not part of it — a cached stream accumulates points across pans.
 */
export interface StreamEntry {
    /** DATA — drives fetching. Part of the cache key. */
    streamId: number;
    /**
     * DATA — drives fetching. The bucket aggregate the API is asked for: the stream's
     * `preferredBucketAggregate`, so an accumulation is summed rather than averaged. Absent means
     * `mean`. Not part of the cache key — it follows from the stream's unit category, not from the
     * panel, so a stream never has two aggregates in play at once.
     */
    aggregate?: BucketAggregate;

    /** VIEW — hex color, assigned by generator or user. */
    color: string;
    /** VIEW — derived stream name from MasterIndex. */
    name: string;
    /**
     * VIEW — which sensor cluster on the device this stream comes from. Two streams off one device in one
     * unit category are identical without it. Absent when no fragment names one.
     */
    sensorClusterName?: string;
    /**
     * VIEW — which channel on that cluster, e.g. "Wind Gust". Two channels on one cluster can land
     * in the same unit category, and this is all that tells them apart. Absent when no fragment
     * names one.
     */
    sensorElementName?: string;
    /** VIEW — references AxisSpec.id within the same panel. */
    axisId: string;
    /**
     * VIEW — what this stream measures. Usually its axis's category too, but a panel can put two
     * categories that share units on one axis, and the axis then names only one of them.
     */
    unitCategory: string;
    /** VIEW — references GroupSpec.id within the same panel. */
    groupId?: string;
    /** VIEW — how this stream is drawn. See TimeAxisPanelSpec for mixed line/bar panels. */
    seriesType: "line" | "bar";
}

// ---------------------------------------------------------------------------
// Bands
// ---------------------------------------------------------------------------

export type LegacyZoneHistorySignal = "scheduled" | "valve-open";
/** DATA. Every field drives fetching and is part of the query key. */
export type BandSourceSpec = { kind: "legacy-zone-history"; zoneId: string; signal: LegacyZoneHistorySignal };

/** A strip of scheduled boxes and valve-open bars above or below the plot. */
export type BandSpec = {
    kind: "scheduled-and-actual";
    scheduled: BandSourceSpec;
    actual: BandSourceSpec;
    /** VIEW. Above the plot, like the legacy strip, or below it, like the mock. Default "below". */
    position?: "above" | "below";
    /** VIEW. Shade the plot under the hovered spans only, or under every span. Default "hover". */
    plotHighlight?: "hover" | "always";
};

// ---------------------------------------------------------------------------
// Panels
// ---------------------------------------------------------------------------

export enum PanelType {
    TimeAxis = "time-axis",
}

/**
 * Shared base for panels that have a time axis.
 * These stack vertically and share synced horizontal scroll/zoom.
 */
export interface TemporalPanelBase {
    id: string;
    title?: string;
    streams: StreamEntry[];
    /**
     * Relative height weight among stacked panels (like CSS flex-grow).
     * Default 1. A panel with height 2 gets twice the vertical space.
     */
    height?: number;
}

/**
 * A panel with a time axis. Streams within the panel declare their own
 * seriesType ("line" | "bar"), allowing mixed charts — e.g. moisture as
 * lines and precipitation as bars on the same panel.
 */
export interface TimeAxisPanelSpec extends TemporalPanelBase {
    type: PanelType.TimeAxis;
    axes: AxisSpec[];
    groups?: GroupSpec[];
    bands?: BandSpec[];
}

/** Additional panel types (depth-offset, gauge, reading) are planned and will widen this union. */
export type PanelSpec = TimeAxisPanelSpec;

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export interface DashboardSpec {
    /** Bump this when making breaking schema changes. */
    version: typeof SPEC_VERSION;
    title?: string;
    description?: string;
    /**
     * Seeds the time range when this spec is first loaded. After that, the active
     * time range lives in session / URL state and is no longer the spec's concern.
     *
     * For link sharing, the recipient's time window comes from the URL params,
     * not this field.
     *
     * Omit to use the application default (e.g. last 7 days).
     */
    defaultTimeRange?: TimeRange;
    /** Rendered top to bottom as one vertical stack in every mode. Order plus `height` is the whole layout. */
    panels: PanelSpec[];
}
